import type { ValidEndpoints } from '@/api/clients/backend-client'
import type { DirectusSettings } from '@/types/directus-types'
import { process_number_or_null, type _rudConverter } from './converter-helpers'
import { fetchCollection, updateItem } from './helper-functions'

const collectionName: ValidEndpoints = 'user_settings'

const process_driver_profile = (
  legacy: Partial<DirectusSettings>,
): Record<string, unknown> | null => {
  const tempObj: {
    acceleration_adjustment?: number | null
    Relative_Speed?: number | null
  } = {}

  if (legacy.acceleration_adjustment != null)
    tempObj.acceleration_adjustment = process_number_or_null(legacy.acceleration_adjustment)
  if (legacy.Relative_Speed != null)
    tempObj.Relative_Speed = process_number_or_null(legacy.Relative_Speed)
  return Object.keys(tempObj).length ? tempObj : null
}

const process_ui_prefs = (legacy: Partial<DirectusSettings>): Record<string, unknown> | null => {
  const tempObj: {
    DSA_change_itinerary_warning?: boolean
    DSA_may_break_trip_warning?: boolean
    DSA_optimise_stops_warning?: boolean
  } = {}

  if (legacy.DSA_change_itinerary_warning) tempObj.DSA_change_itinerary_warning = true
  if (legacy.DSA_may_break_trip_warning) tempObj.DSA_may_break_trip_warning = true
  if (legacy.DSA_optimise_stops_warning) tempObj.DSA_optimise_stops_warning = true

  return Object.keys(tempObj).length ? tempObj : null
}

const process_network_override = (legacy: Partial<DirectusSettings>): string[] | null => {
  if (Array.isArray(legacy.Network_Selection_Override)) return legacy.Network_Selection_Override
  if (typeof legacy.Network_Selection_Override === 'string')
    return [legacy.Network_Selection_Override]
  return null
}

const settingsConverter: _rudConverter<
  User_Settings_Expected_API_DTO,
  DirectusSettings,
  Partial<Omit<DirectusSettings, 'id' | 'user_created'>>
> = {
  apiToLegacy(api) {
    return {
      acceleration_adjustment: process_number_or_null(api.driver_profile?.acceleration_adjustment),
      approval_history: null,
      Default_Extra_Weight: null,
      Diesel_Cost_Per_Litre: null,
      Diesel_Km_Per_Litre: null,
      diesel_road_user_charges: null,
      DSA_change_itinerary_warning: !!api.ui_prefs_optimiser?.DSA_change_itinerary_warning,
      DSA_may_break_trip_warning: !!api.ui_prefs_optimiser?.DSA_may_break_trip_warning,
      DSA_optimise_stops_warning: !!api.ui_prefs_optimiser?.DSA_optimise_stops_warning,
      electric_CO2_kg_per_kWh: null,
      electric_road_user_charges: null,
      Electricity_Cost_Per_Min_Public: null,
      Ending_Charge: null,
      hydrogen_CO2_kg_per_kg: null,
      hydrogen_cost_per_kg: null,
      hydrogen_maintenance_per_annum: null,
      hydrogen_road_user_charges: null,
      id: api.user_id,
      kWh_Cost_Home: null,
      kWh_Cost_Public: null,
      Maximum_Charge: null,
      Minimum_Charge: null,
      Network_Selection_Override: api.network_selection_override,
      passengers: null,
      Per_Annum_Maintenance_Current_V: null,
      Per_Annum_Maintenance_EV: null,
      Petrol_Cost_Per_Litre: null,
      Petrol_Km_Per_Litre: null,
      petrol_road_user_charges: null,
      Relative_Speed: process_number_or_null(api.driver_profile?.Relative_Speed),
      saved_chargers: null,
      selectedVehicleID: api.selected_vehicle,
      Starting_Charge: null,
      user_created: api.user_id,
    }
  },
  legacyToApi(legacy) {
    return {
      driver_profile: process_driver_profile(legacy),
      network_selection_override: process_network_override(legacy),
      ui_prefs_optimiser: process_ui_prefs(legacy),
      user_id: legacy.user_created,
      selected_vehicle: legacy.selectedVehicleID?.toString() ?? null,
    }
  },
  updateToApi(update) {
    const collectorOjb: Partial<User_Settings_Expected_API_DTO> = {}

    if (update.acceleration_adjustment || update.Relative_Speed)
      collectorOjb.driver_profile = process_driver_profile(update)
    if (update.Network_Selection_Override)
      collectorOjb.network_selection_override = process_network_override(update)
    if (
      update.DSA_change_itinerary_warning ||
      update.DSA_may_break_trip_warning ||
      update.DSA_optimise_stops_warning
    )
      collectorOjb.ui_prefs_optimiser = process_ui_prefs(update)
    if (update.selectedVehicleID)
      collectorOjb.selected_vehicle = update.selectedVehicleID?.toString() ?? null

    return collectorOjb
  },
}

type User_Settings_Expected_API_DTO = {
  user_id: string
  driver_profile: Record<string, any> | null
  ui_prefs_optimiser: Record<string, any> | null
  network_selection_override: string[] | null
  selected_vehicle: string | null
}

/**
 * Updates an existing directus `Optimiser_Settings` collection record.
 *
 * @param recordId the directus `Optimiser_Settings` collection record id.
 * @param dataToUpdate partial object to update
 * @returns the updated record if successful, undefined if not.
 */
export async function updateSettingsRecord(
  recordId: string | number,
  dataToUpdate: Partial<Omit<DirectusSettings, 'id' | 'user_created' | 'user_id'>>,
): Promise<DirectusSettings | undefined> {
  return await updateItem<DirectusSettings>({
    endpoint: collectionName,
    query: { user_id: recordId.toString() },
    body: settingsConverter.updateToApi(dataToUpdate),
    converter: settingsConverter.apiToLegacy,
  })
}

/**
 * Fetches the users optimiser settings object if there is one alternatively for first time users
 * creates a record and returns that with the default values.
 *
 * @param userID the user directus user id
 * @returns a `settings` object if successful see types for details. or undefined if an error occurs.
 */
export async function fetchSettings(userID: string): Promise<DirectusSettings | undefined> {
  // attempt to fetch existing record
  const fetchReqRes: DirectusSettings[] | undefined = await fetchCollection<
    User_Settings_Expected_API_DTO,
    DirectusSettings
  >(collectionName, { user_id: userID }, settingsConverter.apiToLegacy)

  // check if a record was retrieved
  if (fetchReqRes?.length) {
    return fetchReqRes[0]
  }

  // // create new record, ASSUMES if error no record exists.
  // const createReqRes = await directusCreateOne<DirectusSettings>(collectionName, {})
  // if (createReqRes) return createReqRes
}
